import { useCallback, useEffect, useRef, useState } from "react";

interface WebSocketHookProps {
  sessionId: string;
  token?: string;
  wsUrl: string;
  onSessionInactive?: () => void;
}

interface LastPingData {
  status?: string;
  uptime?: number;
  downtime?: number;
  last_ping_time?: string;
}

export const useWebSocket = ({
  sessionId,
  token,
  wsUrl,
  onSessionInactive,
}: WebSocketHookProps) => {
  // WebSocket connection state and last ping payload
  const [wsConnected, setWsConnected] = useState<boolean>(() => {
    const v = localStorage.getItem("ws_connected");
    return v ? v === "true" : false;
  });
  const [lastPing, setLastPing] = useState<LastPingData | null>(null);

  // WebSocket management refs
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const sessionRestoredRef = useRef<boolean>(
    localStorage.getItem("ws_connected") === "true" && !!sessionId
  );
  const currentSessionRef = useRef<string | null>(null);
  const isConnectedRef = useRef<boolean>(false);

  // Tab visibility and focus tracking
  const isTabActiveRef = useRef<boolean>(!document.hidden);

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current !== null) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const sendStopAndClose = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try {
      if (ws.readyState === WebSocket.OPEN && sessionId) {
        ws.send(JSON.stringify({ type: "stop", session_id: sessionId }));
      }
    } catch {
      // ignore send errors during teardown
    }
    try {
      ws.close();
    } catch {
      // ignore close errors
    }
    wsRef.current = null;
  }, [sessionId]);

  const sendLogout = useCallback(
    (logoutPayload: {
      type: string;
      user_id: string;
      session_id: string;
      time: string;
    }) => {
      const ws = wsRef.current;
      console.log("🚀 ~ useWebSocket ~ ws:");
      if (!ws) {
        console.warn("[WS] Cannot send logout - WebSocket not connected");
        return false;
      }

      try {
        if (ws.readyState === WebSocket.OPEN) {
          console.log("[WS] Sending logout");
          ws.send(JSON.stringify(logoutPayload));
          return true;
        } else {
          console.warn(
            "[WS] Cannot send logout - WebSocket not open",
            ws.readyState
          );
          return false;
        }
      } catch (error) {
        console.error("[WS] Failed to send logout", error);
        return false;
      }
    },
    []
  );

  // Stable callback for session inactive
  const onSessionInactiveCallback = useCallback(() => {
    if (onSessionInactive) {
      onSessionInactive();
    }
  }, [onSessionInactive]);

  // Handle WebSocket lifecycle when sessionId is set
  useEffect(() => {
    if (!sessionId || !token) return;

    const connectionKey = `${sessionId}-${token}`;

    // If we already have a connection for this exact session/user, don't create another
    if (
      currentSessionRef.current === connectionKey &&
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      console.log("[WS] WebSocket already exists for this session, skipping");
      return;
    }

    // Close existing connection if it's for a different session
    if (wsRef.current && currentSessionRef.current !== connectionKey) {
      console.log("[WS] Closing connection for different session");
      wsRef.current.close();
      wsRef.current = null;
      isConnectedRef.current = false;
    }

    // Only create new connection if we don't have one or it's closed
    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      currentSessionRef.current = connectionKey;

      const fullWsUrl = `${wsUrl}/?token=${token}`;
      console.log("[WS] Creating WebSocket connection");
      const ws = new WebSocket(fullWsUrl);
      wsRef.current = ws;

      const sendStart = () => {
        try {
          console.log("[WS] Sending 'start'");
          ws.send(
            JSON.stringify({
              type: "start",
              session_id: sessionId,
            })
          );
        } catch {
          console.warn("[WS] Failed to send 'start' (socket not ready yet)");
        }
      };

      const sendPing = () => {
        try {
          if (ws.readyState === WebSocket.OPEN) {
            const payload = {
              type: "ping",
              session_id: sessionId,
              ts: Date.now(),
            } as const;
            console.log("[WS] Sending 'ping'");
            ws.send(
              JSON.stringify({
                ...payload,
              })
            );
          } else {
            console.debug("[WS] Ping skipped; socket not open", ws.readyState);
          }
        } catch {
          console.warn("[WS] Failed to send 'ping' (socket not ready yet)");
        }
      };

      let pingsEnabled = !document.hidden;

      const ensurePingTimer = () => {
        if (pingsEnabled && pingIntervalRef.current === null) {
          console.log("[WS] Starting ping interval (60s)");
          pingIntervalRef.current = window.setInterval(sendPing, 60_000);
        }
      };

      ws.onopen = () => {
        console.log("[WS] Socket open");
        isConnectedRef.current = true;
        // Only send 'start' if session was not restored from storage
        if (!sessionRestoredRef.current) {
          sendStart();
        } else {
          console.log("[WS] Session restored; sending initial ping after 1s");
          setTimeout(() => {
            sendPing();
          }, 1000);
        }
        clearPingInterval();
        ensurePingTimer();
        setWsConnected(true);
        localStorage.setItem("ws_connected", "true");
      };

      ws.onclose = (evt) => {
        console.log("[WS] Socket close", {
          code: evt.code,
          reason: evt.reason,
        });
        isConnectedRef.current = false;
        clearPingInterval();
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        setWsConnected(false);
        localStorage.setItem("ws_connected", "false");
      };

      ws.onerror = (evt) => {
        console.warn("[WS] Socket error", evt);
        isConnectedRef.current = false;
      };

      ws.onmessage = (evt) => {
        try {
          console.debug("[WS] Message received", evt.data);
          const data = JSON.parse(evt.data || "{}");
          // Expecting: { status: 'pong', uptime, downtime, last_ping_time, is_active }
          if (
            data &&
            (data.status === "pong" || typeof data.uptime !== "undefined")
          ) {
            console.log("[WS] Pong payload");
            setLastPing(data);

            // Check if session is inactive and trigger logout
            if (data.is_active === false) {
              console.warn(
                "[WS] Session marked as inactive, triggering logout"
              );
              onSessionInactiveCallback();
            }
          }
        } catch {
          console.warn("[WS] Failed to parse message payload");
        }
      };

      const handleVisibilityChange = () => {
        const isHidden = document.hidden;
        isTabActiveRef.current = !isHidden;
        pingsEnabled = !isHidden;

        console.log("[WS] Tab visibility change - hidden:");

        if (pingsEnabled) {
          console.log("[WS] Tab visible; resume pings");
          // send one immediate ping when resuming if open
          if (ws.readyState === WebSocket.OPEN) sendPing();
          clearPingInterval();
          ensurePingTimer();
        } else {
          console.log("[WS] Tab hidden; pause pings");
          clearPingInterval();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Schedule ping timer proactively; will no-op if hidden
      console.log("[WS] Ensuring ping timer on mount");
      ensurePingTimer();
    }

    return () => {
      console.log("[WS] Cleanup effect; removing listeners");
      clearPingInterval();
    };
  }, [sessionId, token, wsUrl, clearPingInterval, onSessionInactiveCallback]); // Essential dependencies

  const resetSessionRestored = useCallback(() => {
    sessionRestoredRef.current = false;
  }, []);

  return {
    wsConnected,
    lastPing,
    clearPingInterval,
    sendStopAndClose,
    sendLogout,
    resetSessionRestored,
  };
};
